import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import FilterBar, { FilterField } from 'components/Common/FilterBar';
import {
    Button,
    Flex,
    Popconfirm,
    Tag,
    Select,
    Space,
    Tooltip,
    Modal,
    List,
    Typography,
    message
} from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
    EditOutlined,
    DeleteOutlined,
    CopyOutlined,
    ExclamationCircleOutlined,
    HistoryOutlined,
    PushpinOutlined
} from '@ant-design/icons';
import * as actions from 'store/actions/AuthActions';
import useUrlFilters from 'hooks/useUrlFilters';

const { Option } = Select;
const { Text } = Typography;

// Filters, search and paging live in the URL so a filtered list can be shared as a link.
const URL_FILTERS = {
    search: { type: 'string' },
    status: { type: 'string' },
    type: { type: 'string' },
    category: { type: 'string' },
    page: { type: 'number', default: 1 },
    pageSize: { type: 'number', key: 'page_size', default: 50 },
};

const LinksTable = () => {
    const navigate = useNavigate();
    const [filters, setFilters] = useUrlFilters(URL_FILTERS);
    // Any filter change goes back to the first page.
    const urlSetter = (name) => (value) => setFilters({ [name]: value, page: 1 });

    // State
    const searchTerm = filters.search || '';
    const setSearchTerm = urlSetter('search');
    const [searchTermLive, setSearchTermLive] = useState(searchTerm);
    // Keep the box in step when the URL changes underneath it (back/forward).
    useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);
    const statusFilter = filters.status || '';
    const setStatusFilter = urlSetter('status');
    const typeFilter = filters.type || '';
    const setTypeFilter = urlSetter('type');
    const categoryFilter = filters.category || '';
    const setCategoryFilter = urlSetter('category');

    const currentPage = filters.page;
    const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
    const [totalRows, setTotalRows] = useState(0);

    const [allLinks, setAllLinks] = useState([]);
    const [dataSource, setDataSource] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);

    // Version history modal
    const [versionModalOpen, setVersionModalOpen] = useState(false);
    const [selectedLink, setSelectedLink] = useState(null);
    const [versions, setVersions] = useState([]);
    const [loadingVersions, setLoadingVersions] = useState(false);

    const statusOptions = [
        { value: 'draft', label: 'Draft', color: 'default' },
        { value: 'active', label: 'Active', color: 'green' },
        { value: 'deprecated', label: 'Deprecated', color: 'orange' },
        { value: 'archived', label: 'Archived', color: 'red' },
    ];

    const typeOptions = [
        { value: 'internal', label: 'Internal' },
        { value: 'external', label: 'External' },
        { value: 'documentation', label: 'Documentation' },
        { value: 'api', label: 'API Endpoint' },
        { value: 'resource', label: 'Resource' },
        { value: 'tool', label: 'Tool' },
    ];

    useEffect(() => {
        loadCategories();
    }, []);

    useEffect(() => {
        fetchAllLinks();
    }, [searchTerm, statusFilter, typeFilter, categoryFilter]);

    useEffect(() => {
        paginateData();
    }, [allLinks, currentPage, pageSize]);

    const loadCategories = async () => {
        const data = await actions.loadAllLinkCategories();
        setCategories(data || []);
    };

    const fetchAllLinks = async () => {
        setLoading(true);
        try {
            const response = await actions.loadAllLinks(searchTerm, {
                status: statusFilter,
                link_type: typeFilter,
                category: categoryFilter,
            });
            if (response.status === 200) {
                const data = response.data;
                setAllLinks(data.results || data || []);
                setTotalRows(data.count || data?.length || 0);
            }
        } catch (e) {
            console.error('Error fetching links:', e);
        }
        setLoading(false);
    };

    const paginateData = () => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedData = allLinks.slice(startIndex, endIndex);
        setDataSource(paginatedData);
    };

    const onSearch = (value) => {
        setSearchTerm(value);
    };

    // Search sits outside the filter panel, so it is neither counted nor cleared.
    const activeFilterCount =
        (statusFilter ? 1 : 0) + (typeFilter ? 1 : 0) + (categoryFilter ? 1 : 0);

    const clearFilters = () => {
        setFilters({ status: null, type: null, category: null, page: 1 });
    };

    const handleDelete = async (id) => {
        const response = await actions.deleteLink(id);
        if (response.status === 204) {
            fetchAllLinks();
        }
    };

    const handleOpenLink = async (record) => {
        await actions.recordLinkClick(record.id);
        window.open(record.url, '_blank');
    };

    const handleShowVersions = async (record) => {
        setSelectedLink(record);
        setVersionModalOpen(true);
        setLoadingVersions(true);
        const versionData = await actions.getLinkVersions(record.id);
        setVersions(versionData || []);
        setLoadingVersions(false);
    };

    const handleRestoreVersion = async (versionId) => {
        if (selectedLink) {
            await actions.restoreLinkVersion(selectedLink.id, versionId);
            setVersionModalOpen(false);
            fetchAllLinks();
        }
    };

    const getStatusTag = (status) => {
        const statusOption = statusOptions.find(s => s.value === status);
        return <Tag color={statusOption?.color || 'default'}>{statusOption?.label || status}</Tag>;
    };

    const columns = useMemo(() => [
        {
            title: '',
            dataIndex: 'is_pinned',
            key: 'is_pinned',
            width: 40,
            render: (isPinned) => isPinned ? <PushpinOutlined style={{ color: '#1890ff' }} /> : null,
        },
        {
            title: 'TITLE',
            dataIndex: 'title',
            key: 'title',
            sorter: (a, b) => a?.title?.localeCompare(b?.title),
            width: 220,
            render: (title, record) => (
                <Space>
                    <a onClick={() => handleOpenLink(record)} style={{ cursor: 'pointer' }}>
                        {title}
                    </a>
                    {record.is_broken && (
                        <Tooltip title="Link is broken">
                            <ExclamationCircleOutlined style={{ color: 'red' }} />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        {
            title: 'URL',
            dataIndex: 'slug',
            key: 'redirect_url',
            width: 250,
            ellipsis: true,
            render: (slug) => {
                const redirectUrl = `${window.location.origin}/l/${slug}/`;
                return (
                    <Tooltip title={redirectUrl}>
                        <a href={redirectUrl} target="_blank" rel="noopener noreferrer">
                            {redirectUrl}
                        </a>
                    </Tooltip>
                );
            },
        },
        {
            title: 'CATEGORY',
            dataIndex: 'category_name',
            key: 'category_name',
            width: 120,
            render: (name) => name || '-',
        },
        {
            title: 'TYPE',
            dataIndex: 'link_type',
            key: 'link_type',
            width: 100,
            render: (type) => {
                const typeOption = typeOptions.find(t => t.value === type);
                return typeOption?.label || type;
            },
        },
        {
            title: 'STATUS',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => getStatusTag(status),
        },
        {
            title: 'VERSION',
            dataIndex: 'current_version',
            key: 'current_version',
            width: 80,
            render: (version, record) => (
                <Tooltip title={`${record.version_count || 1} version(s)`}>
                    <span>v{version}</span>
                </Tooltip>
            ),
        },
        {
            title: 'CLICKS',
            dataIndex: 'click_count',
            key: 'click_count',
            width: 80,
            sorter: (a, b) => a.click_count - b.click_count,
        },
        {
            title: 'TAGS',
            dataIndex: 'tags',
            key: 'tags',
            width: 150,
            render: (tags) => (
                <Space wrap size={[0, 4]}>
                    {tags?.slice(0, 2).map((tag, index) => (
                        <Tag key={index} color="blue">{tag}</Tag>
                    ))}
                    {tags?.length > 2 && <Tag>+{tags.length - 2}</Tag>}
                </Space>
            ),
        },
        {
            title: 'ACTIONS',
            dataIndex: 'actions',
            key: 'actions',
            width: 180,
            render: (_, record) => (
                <Flex gap="small">
                    <Tooltip title="Copy Redirect URL">
                        <Button
                            type="text"
                            icon={<CopyOutlined />}
                            onClick={() => {
                                const url = `${window.location.origin}/l/${record.slug}/`;
                                navigator.clipboard.writeText(url);
                                message.success('Redirect URL copied');
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Version History">
                        <Button
                            type="text"
                            icon={<HistoryOutlined />}
                            onClick={() => handleShowVersions(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Edit">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => navigate(`/links/edit/${record.id}`)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Sure to delete?"
                        onConfirm={() => handleDelete(record.id)}
                    >
                        <Tooltip title="Delete">
                            <Button type="text" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Flex>
            ),
        },
    ], [categories, navigate]);

    return (
        <div style={{ padding: 10 }}>
            <div style={{ marginBottom: 8 }}>
              Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalRows)} of {totalRows} links
      </div>
            <Flex gap="large" justify="space-between" align="flex-start" wrap>
                <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <FilterBar
                        searchValue={searchTermLive}
                        onSearchChange={setSearchTermLive}
                        onSearch={onSearch}
                        searchPlaceholder="Search links"
                        activeCount={activeFilterCount}
                        onClear={clearFilters}
                        title="Filter Links"
                        addLabel="Add Link"
                        onAdd={() => navigate('/links/new')}
                        onRefresh={() => fetchAllLinks()}
                        refreshing={loading}
                    >
                        <FilterField label="Status">
                            <Select
                                placeholder="Status"
                                allowClear
                                value={statusFilter || undefined}
                                onChange={(value) => {
                                    setStatusFilter(value || '');
                                }}
                            >
                                {statusOptions.map(s => (
                                    <Option key={s.value} value={s.value}>{s.label}</Option>
                                ))}
                            </Select>
                        </FilterField>
                        <FilterField label="Type">
                            <Select
                                placeholder="Type"
                                allowClear
                                value={typeFilter || undefined}
                                onChange={(value) => {
                                    setTypeFilter(value || '');
                                }}
                            >
                                {typeOptions.map(t => (
                                    <Option key={t.value} value={t.value}>{t.label}</Option>
                                ))}
                            </Select>
                        </FilterField>
                        <FilterField label="Category">
                            <Select
                                placeholder="Category"
                                allowClear
                                value={categoryFilter || undefined}
                                onChange={(value) => {
                                    setCategoryFilter(value || '');
                                }}
                            >
                                {categories.map(c => (
                                    <Option key={c.id} value={c.id}>{c.name}</Option>
                                ))}
                            </Select>
                        </FilterField>
                    </FilterBar>
                </div>
                <Flex gap="middle">
                    <Button onClick={() => navigate('/links/categories')}>
                        Manage Categories
                    </Button>
                </Flex>
            </Flex>

            <div style={{ marginTop: 20 }}>
                <Table
                    loading={loading}
                    bordered
                    size="small"
                    dataSource={dataSource}
                    columns={columns}
                    rowKey={row => row.id}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalRows,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        pageSizeOptions: ['10', '25', '50', '100'],
                        onChange: (page, size) => {
                            setFilters({ page: size !== pageSize ? 1 : page, pageSize: size });
                        },
                    }}
                    scroll={{
                        x: 'max-content',
                        y: 'calc(100vh - 350px)',
                    }}
                    sticky
                />
            </div>

            {/* Version History Modal */}
            <Modal
                title={`Version History - ${selectedLink?.title}`}
                open={versionModalOpen}
                onCancel={() => setVersionModalOpen(false)}
                footer={null}
                width={700}
            >
                <List
                    loading={loadingVersions}
                    dataSource={[
                        // Current version
                        selectedLink && {
                            id: 'current',
                            version: selectedLink.current_version,
                            title: selectedLink.title,
                            url: selectedLink.url,
                            created_at: selectedLink.updated_at,
                            isCurrent: true,
                        },
                        ...versions,
                    ].filter(Boolean)}
                    renderItem={(item) => (
                        <List.Item
                            actions={
                                !item.isCurrent ? [
                                    <Button
                                        key="restore"
                                        type="link"
                                        onClick={() => handleRestoreVersion(item.id)}
                                    >
                                        Restore
                                    </Button>
                                ] : [
                                    <Tag key="current" color="green">Current</Tag>
                                ]
                            }
                        >
                            <List.Item.Meta
                                title={
                                    <Space>
                                        <Text strong>v{item.version}</Text>
                                        <Text type="secondary">{item.title}</Text>
                                    </Space>
                                }
                                description={
                                    <Space direction="vertical" size={0}>
                                        <Text ellipsis style={{ maxWidth: 400 }}>{item.url}</Text>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {new Date(item.created_at).toLocaleString()}
                                        </Text>
                                    </Space>
                                }
                            />
                        </List.Item>
                    )}
                />
            </Modal>
        </div>
    );
};

export default LinksTable;
